import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';
import { Leaf, FolderTree, Award, ChevronRight, ShieldAlert, ArrowLeft, Database, MapPin } from 'lucide-react';
import { useModuleLicense } from '../../hooks/useModuleLicense';

interface AdminTile {
  title: string;
  description: string;
  icon: React.ReactNode;
  to: string;
}

const TILES: AdminTile[] = [
  {
    title: 'Crop Types',
    description: 'Manage crop categories and subcategories (e.g. Stone Fruit › Peaches)',
    icon: <FolderTree className="h-6 w-6 text-primary-600" />,
    to: '/cultivar/admin/crop-types',
  },
  {
    title: 'Programs',
    description: 'Royalty / licensing programs (Annual Tree Royalty, Production Royalty, …) and their rules',
    icon: <Award className="h-6 w-6 text-primary-600" />,
    to: '/cultivar/admin/programs',
  },
  {
    title: 'Attribute Definitions',
    description: 'Manage the global + custom attributes used in cultivar observations (Brix, fruit size, disease resistance, …)',
    icon: <Database className="h-6 w-6 text-primary-600" />,
    to: '/cultivar/admin/attributes',
  },
  {
    title: 'Growing Regions',
    description: 'Climate areas that harvest together (Goulburn Valley, Batlow, …) — what maturity dates are recorded against',
    icon: <MapPin className="h-6 w-6 text-primary-600" />,
    to: '/cultivar/admin/growing-regions',
  },
];

function CultivarAdmin() {
  const navigate = useNavigate();
  const { hasAccess, loading } = useModuleLicense('cultivar', 'admin');

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader
          title="Cultivar Admin"
          subtitle="Administrator tools for the cultivar registry"
          icon={<Leaf className="h-6 w-6 text-white" />}
        />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 text-center text-neutral-600">
          Loading…
        </div>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader
          title="Cultivar Admin"
          subtitle="Administrator tools for the cultivar registry"
          icon={<Leaf className="h-6 w-6 text-white" />}
        />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
          <div className="bg-white rounded-xl shadow-sm border border-neutral-200 p-8 text-center">
            <div className="h-12 w-12 rounded-full bg-error-100 flex items-center justify-center mx-auto mb-4">
              <ShieldAlert className="h-6 w-6 text-error-600" />
            </div>
            <h3 className="text-lg font-semibold text-neutral-900 mb-2">Cultivar Admin licence required</h3>
            <p className="text-neutral-600 mb-6">This area is restricted to users with a Cultivar Admin licence seat.</p>
            <Button variant="outline" onClick={() => navigate('/cultivar')}>
              <ArrowLeft className="h-4 w-4" />
              Back to Cultivar Registry
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Cultivar Admin"
        subtitle="Administrator tools for the cultivar registry"
        icon={<Leaf className="h-6 w-6 text-white" />}
        actionButtons={
          <Button variant="outline" onClick={() => navigate('/cultivar')}>
            <ArrowLeft className="h-4 w-4" />
            Back to Registry
          </Button>
        }
      />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {TILES.map((tile) => (
            <Card
              key={tile.to}
              className="hover:shadow-md transition-shadow duration-200 cursor-pointer"
              onClick={() => navigate(tile.to)}
            >
              <CardContent className="p-5">
                <div className="flex items-start gap-3">
                  <div className="h-12 w-12 rounded-lg bg-primary-50 flex items-center justify-center flex-shrink-0">
                    {tile.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="font-semibold text-neutral-900">{tile.title}</h3>
                      <ChevronRight className="h-4 w-4 text-neutral-400 flex-shrink-0" />
                    </div>
                    <p className="mt-1 text-sm text-neutral-600">{tile.description}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}

export default CultivarAdmin;
